<template>
    <VcForm
        ref="editWidgetForm"
        :model="localModel"
        :show-message="false"
        class="widget-external-edit-form"
    >
        <VcCollapse v-model="activeCollapse">
            <RedesignFormItem
                :label="t('widget.title')"
                :rules="[required]"
                prop="Title"
            >
                <VcInput v-model="localModel.Title" />
            </RedesignFormItem>
            <RedesignFormItem
                v-if="'DefaultRefreshInterval' in localModel"
                :label="t('widget.widgetRefreshInterval')"
                :info-text="t('widget.setIntervalOnWidgetReload')"
            >
                <RedesignWidgetRefreshInterval
                    v-model="widgetLayoutModel.DefaultRefreshInterval"
                    :min-value="minIntervalValue"
                    :max-value="maxIntervalValue"
                />
            </RedesignFormItem>
            <RedesignFormItem
                :label="t('widget.widgetExternalEndpoint')"
            >
                <VcInput
                    v-model="widgetLayoutModel.Endpoint"
                    type="textarea"
                    :autosize="{ minRows: 1, maxRows: 4}"
                />
            </RedesignFormItem>
            <RedesignFormItem
                :label="t('widget.widgetType')"
            >
                <VcSelect
                    v-model="widgetLayoutModel.ComponentTypeID"
                    placeholder="Select"
                    :options="componentTypeOptions"
                    :multiple="false"
                    :config="{
                        labelKey: 'label',
                        valueKey: 'value'
                    }"
                    class="w-full"
                />
            </RedesignFormItem>
            <div class="mb-5">
                <VcCollapseItem
                    v-if="widgetLayoutModel.ComponentTypeID "
                    :title="t('widget.dictionary')"
                    name="dictionary"
                    class="simple-collapse"
                >
                    <div class="px-1">
                        <json-viewer
                            :value="jsonViewerData"
                            :expand-depth="5"
                            copyable
                            boxed
                            sort
                        />
                    </div>
                </VcCollapseItem>
            </div>
            <div class="mb-5">
                <VcCollapseItem
                    :title="t('widget.layout')"
                    name="layout"
                    class="simple-collapse"
                >
                    <RedesignFormItem
                        :label="t('widget.widgetPadding')"
                    >
                        <VcInputNumber
                            v-model="widgetLayoutModel.padding"
                            :step="1"
                            :min="0"
                            placeholder="0"
                        />
                    </RedesignFormItem>
                    <RedesignWidgetColors
                        v-model="colorsModel"
                        :available-colors="availableColors"
                    />
                </VcCollapseItem>
            </div>
        </VcCollapse>
    </VcForm>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { VcForm, VcCollapse, VcCollapseItem, VcInput, VcSelect, VcInputNumber } from '@voicenter-team/voicenter-ui-plus'
import { widgetModelData } from '@/composables/useWidgetEditModal'
import RedesignFormItem from '@/components/RedesignBaseComponents/Form/RedesignFormItem.vue'
import useValidationRules from '@/composables/useValidationRules'
import RedesignWidgetRefreshInterval
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetRefreshInterval.vue'
import get from 'lodash/get'
import { dictionary, options } from '@/enum/externalDataWidgetConfig'
import JsonViewer from 'vue-json-viewer'
import RedesignWidgetColors from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetColors.vue'
import { WidgetExtended } from '@/types/dashboard'
import { getWidgetLayoutDefaultColors } from '@/components/RedesignComponents/Widgets/widget.helper'

const { t } = useI18n()
const { required } = useValidationRules()

const editWidgetForm = ref<InstanceType<typeof VcForm>>()

/* Props */
const props = defineProps<{
    modelValue: WidgetExtended
}>()
/* Emits */
const emit = defineEmits<{
    (e: 'update:modelValue', value: WidgetExtended): void
}>()

/* Form Model */
const localModel = computed({
    get () {
        return props.modelValue
    },
    set (val) {
        emit('update:modelValue', val)
    }
})
const colorsModel = computed({
    get () {
        return widgetLayoutModel.value.colors
    },
    set (val) {
        widgetLayoutModel.value.colors = { ...val }
    }
})
const widgetLayoutModel = computed({
    get () {
        return localModel.value.WidgetLayout
    },
    set (val) {
        localModel.value.WidgetLayout = { ...val }
    }
})

/* Data */
const activeCollapse = ref([ '' ])

/* Computed */
const templateSettings = computed(() => {
    return get(widgetModelData.value, 'WidgetLayout.TemplateSettings', {
        MinimumRefreshInterval: 30,
        MaximumRefreshInterval: 1800,
    })
})
const minIntervalValue = computed(() => {
    return Number(templateSettings.value.MinimumRefreshInterval)
})
const maxIntervalValue = computed(() => {
    return Number(templateSettings.value.MaximumRefreshInterval)
})
const componentTypeOptions = computed(() => {
    return options
})
const jsonViewerData = computed(() => {
    if (!localModel.value?.WidgetLayout?.ComponentTypeID) {
        return ''
    }
    return dictionary[localModel.value.WidgetLayout.ComponentTypeID] || ''
})
const availableColors = computed(() => {
    return getWidgetLayoutDefaultColors(props.modelValue) || {}
})

/* Methods */
async function validate () {
    const res = await editWidgetForm.value?.validate()
    return res?.isValid
}

defineExpose({
    validate
})
</script>

<style lang="scss">
.widget-external-edit-form {
    .el-checkbox, .el-checkbox__input {
        white-space: normal;
    }
}
</style>
